import { useEffect, useState } from "react";
import axios from "axios";
import { Instagram } from "lucide-react";
import SocialIcon from "@/components/SocialIcon";
import { SOCIALS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IG_URL = "https://www.instagram.com/jinsu.fi";

export default function InstagramFeed() {
  const [state, setState] = useState({ loading: true, posts: [] });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/instagram/feed`)
      .then((res) => {
        if (!cancelled) setState({ loading: false, posts: res.data.data || [] });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, posts: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { loading, posts } = state;

  return (
    <section data-testid="instagram-section" className="py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow text-water-deep/80">Instagram</p>
            <h2 className="h-section font-heading mt-5 font-semibold text-foreground" lang="fi">
              Seuraa meitä
            </h2>
          </div>
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-link"
            className="inline-flex items-center gap-2.5 rounded-full border border-primary/25 bg-white px-6 py-3 text-sm font-bold text-primary transition-colors duration-200 hover:bg-secondary"
          >
            <Instagram size={17} /> @jinsu.fi
          </a>
        </div>

        {loading ? (
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="instagram-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-3xl bg-water-soft" />
            ))}
          </div>
        ) : posts.length > 0 ? (
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="instagram-grid">
            {posts.slice(0, 6).map((post) => {
              const image = post.media_type === "VIDEO" ? post.thumbnail_url : post.media_url || post.thumbnail_url;
              return (
                <a
                  key={post.id}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`instagram-post-${post.id}`}
                  className="group relative block overflow-hidden rounded-3xl bg-water-soft"
                >
                  {image && (
                    <img
                      src={image}
                      alt={post.caption ? post.caption.slice(0, 120) : "JINSU Instagram -julkaisu"}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <span className="absolute inset-0 flex items-end bg-gradient-to-t from-primary/70 via-transparent to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="line-clamp-2 text-xs font-semibold text-white">
                      {post.caption ? post.caption.slice(0, 80) : "Katso Instagramissa"}
                    </span>
                  </span>
                </a>
              );
            })}
          </div>
        ) : (
          <div className="mt-12 flex items-center justify-center gap-4" data-testid="instagram-fallback">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${s.name}: ${s.label}`}
                data-testid={`ig-fallback-${s.name.toLowerCase()}`}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-water-soft text-water-deep shadow-sm transition-all duration-200 hover:scale-110 hover:bg-water"
              >
                <SocialIcon name={s.name} size={24} strokeWidth={1.7} />
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
